import { OfficeHoursMode, FilterField, FilterOperator, } from '../models/workflow-dtos.js';
import { RelationshipAccessLevel } from '../crm/relationship.js';
import { normalizePropertyScope } from './filter-conditions.js';
const KNOWN_GROUP_TYPES = new Set(Object.values(RelationshipAccessLevel));
function isKnownGroupType(value) {
    return KNOWN_GROUP_TYPES.has(value);
}
/** API/payload uses lowercase-with-hyphen strings; convert enum to that. */
export function officeHoursToApiValue(value) {
    switch (value) {
        case OfficeHoursMode.BUSINESS_HOURS:
            return 'business-hours';
        case OfficeHoursMode.AFTER_HOURS:
            return 'after-hours';
        case OfficeHoursMode.CUSTOM_HOURS:
            return 'custom-hours';
        default:
            return 'always';
    }
}
/** Parse API/payload string into the enum. */
export function parseOfficeHours(value) {
    switch (value) {
        case 'business-hours':
        case OfficeHoursMode.BUSINESS_HOURS:
            return OfficeHoursMode.BUSINESS_HOURS;
        case 'after-hours':
        case OfficeHoursMode.AFTER_HOURS:
            return OfficeHoursMode.AFTER_HOURS;
        case 'custom-hours':
        case OfficeHoursMode.CUSTOM_HOURS:
            return OfficeHoursMode.CUSTOM_HOURS;
        case 'always':
        case OfficeHoursMode.ALWAYS:
            return OfficeHoursMode.ALWAYS;
        default:
            return OfficeHoursMode.ALWAYS;
    }
}
/** Human-readable labels for UI. */
export const OFFICE_HOURS_LABELS = {
    [OfficeHoursMode.ALWAYS]: 'Always',
    [OfficeHoursMode.BUSINESS_HOURS]: 'Business Hours Only',
    [OfficeHoursMode.AFTER_HOURS]: 'After Hours Only',
    [OfficeHoursMode.CUSTOM_HOURS]: 'Custom Times',
};
export function getOfficeHoursLabel(value) {
    return OFFICE_HOURS_LABELS[value] ?? 'Always';
}
const VALID_OFFICE_HOURS = new Set([
    OfficeHoursMode.ALWAYS,
    OfficeHoursMode.BUSINESS_HOURS,
    OfficeHoursMode.AFTER_HOURS,
    OfficeHoursMode.CUSTOM_HOURS,
]);
/**
 * Read office hours from filter conditions. Handles both enum values
 * (ALWAYS, BUSINESS_HOURS, AFTER_HOURS) and API strings (always, business-hours, ...).
 */
export function getOfficeHoursFromFilterConditions(conditions) {
    const cond = conditions?.find(c => c.field === FilterField.OFFICE_HOURS);
    const val = cond?.value;
    if (val == null)
        return undefined;
    if (typeof val === 'string' && VALID_OFFICE_HOURS.has(val))
        return val;
    if (typeof val === 'string')
        return parseOfficeHours(val);
    return undefined;
}
/**
 * Convert office hours enum to filter conditions for API/storage.
 * Returns null when ALWAYS or undefined (no condition needed).
 */
export function officeHoursToFilterConditions(officeHours) {
    if (!officeHours || officeHours === OfficeHoursMode.ALWAYS)
        return null;
    return [
        {
            field: FilterField.OFFICE_HOURS,
            operator: FilterOperator.EQUALS,
            value: officeHours,
        },
    ];
}
/**
 * Read property IDs from the PROPERTY_SCOPE condition.
 * Returns empty array if no property filter is set (means "all properties").
 */
export function getPropertyIdsFromFilterConditions(conditions) {
    const scope = normalizePropertyScope(conditions).find(c => c.field === FilterField.PROPERTY_SCOPE);
    const value = scope?.value;
    return value?.propertyIds ?? [];
}
/**
 * Read property-group IDs from the PROPERTY_SCOPE condition.
 * Returns empty array if no group filter is set.
 */
export function getPropertyGroupIdsFromFilterConditions(conditions) {
    const scope = normalizePropertyScope(conditions).find(c => c.field === FilterField.PROPERTY_SCOPE);
    const value = scope?.value;
    return value?.groupIds ?? [];
}
/**
 * Read group types from filter conditions.
 * Returns empty array if no group filter is set (means "all groups").
 * Filters out values that are not a known RelationshipAccessLevel so
 * downstream UI code can rely on the typed return.
 */
export function getGroupTypesFromFilterConditions(conditions) {
    const cond = conditions?.find(c => c.field === FilterField.GROUP_TYPES);
    if (!cond || !Array.isArray(cond.value))
        return [];
    return cond.value.filter((v) => typeof v === 'string' && isKnownGroupType(v));
}
/**
 * Canonical string representation of a filter-condition set, suitable for
 * equality comparison. Returns '' for empty/null/undefined.
 * Order-independent across the full tuple: sorts by (field, operator,
 * stringified value) so reorderings of same-field/different-operator
 * conditions collapse to the same string.
 */
export function normalizeFilterConditions(conditions) {
    if (!conditions || conditions.length === 0)
        return '';
    const tuples = conditions.map(c => {
        const v = Array.isArray(c.value)
            ? [...new Set(c.value.map(String))].sort()
            : typeof c.value === 'object' && c.value !== null
                ? JSON.stringify({
                    propertyIds: [...new Set(c.value.propertyIds ?? [])].sort(),
                    groupIds: [...new Set(c.value.groupIds ?? [])].sort(),
                })
                : String(c.value);
        return [c.field, c.operator, v];
    });
    tuples.sort((a, b) => {
        if (a[0] !== b[0])
            return a[0] < b[0] ? -1 : 1;
        if (a[1] !== b[1])
            return a[1] < b[1] ? -1 : 1;
        const av = JSON.stringify(a[2]);
        const bv = JSON.stringify(b[2]);
        return av < bv ? -1 : av > bv ? 1 : 0;
    });
    return JSON.stringify(tuples);
}
/**
 * Build filter conditions array from office hours, property IDs, and group types.
 * Omits conditions that mean "all" (no restriction).
 */
export function buildFilterConditions(opts) {
    const conditions = [];
    if (opts.officeHours && opts.officeHours !== OfficeHoursMode.ALWAYS) {
        conditions.push({
            field: FilterField.OFFICE_HOURS,
            operator: FilterOperator.EQUALS,
            value: opts.officeHours,
        });
    }
    // Property scope is one condition owning both axes (properties OR groups).
    const hasPropertyIds = !!opts.propertyIds?.length;
    const hasGroupIds = !!opts.propertyGroupIds?.length;
    if (hasPropertyIds || hasGroupIds) {
        conditions.push({
            field: FilterField.PROPERTY_SCOPE,
            operator: FilterOperator.IN,
            value: {
                ...(hasPropertyIds && { propertyIds: opts.propertyIds }),
                ...(hasGroupIds && { groupIds: opts.propertyGroupIds }),
            },
        });
    }
    if (opts.groupTypes && opts.groupTypes.length > 0) {
        conditions.push({
            field: FilterField.GROUP_TYPES,
            operator: FilterOperator.IN,
            value: opts.groupTypes,
        });
    }
    return conditions.length > 0 ? conditions : null;
}
/**
 * Field-priority order for tiebreak. Earlier in the list = higher priority.
 * GROUP_TYPES > PROPERTY_SCOPE > OFFICE_HOURS.
 */
export const FILTER_FIELD_PRIORITY = [
    FilterField.GROUP_TYPES,
    FilterField.PROPERTY_SCOPE,
    FilterField.OFFICE_HOURS,
];
function uniqueFieldsByPriority(c) {
    // normalizePropertyScope merges scope entries, so property scope counts as
    // exactly one field regardless of how many axes it uses.
    const fields = normalizePropertyScope(c.filterConditions).map(cond => cond.field);
    return [...new Set(fields)]
        .map(f => FILTER_FIELD_PRIORITY.indexOf(f))
        .filter(i => i !== -1)
        .sort((a, b) => a - b);
}
/**
 * Step 1: more unique filter fields wins.
 */
export function compareByFieldCount(a, b) {
    const ca = uniqueFieldsByPriority(a).length;
    const cb = uniqueFieldsByPriority(b).length;
    if (ca === cb)
        return 0;
    return ca > cb ? -1 : 1;
}
/**
 * Step 2: when scenarios tie on field count, the scenario with the
 * higher-priority field set wins. Walks both scenarios' field sets in priority
 * order (GROUP_TYPES > PROPERTY_SCOPE > OFFICE_HOURS) and decides on the first
 * position where the priorities differ. Returns 0 only when the field sets
 * are identical (same length, same fields). If lengths differ (i.e. one is a
 * strict subset of the other), the shorter set wins on the first position
 * past its end so the docstring's "identical only" promise holds standalone.
 */
export function compareByFieldPriority(a, b) {
    const pa = uniqueFieldsByPriority(a);
    const pb = uniqueFieldsByPriority(b);
    const len = Math.min(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
        if (pa[i] !== pb[i])
            return pa[i] < pb[i] ? -1 : 1;
    }
    if (pa.length === pb.length)
        return 0;
    return pa.length < pb.length ? -1 : 1;
}
/**
 * Step 3: oldest createdAt wins (final stable tiebreak).
 */
export function compareByCreatedAt(a, b) {
    const ta = a.createdAt.getTime();
    const tb = b.createdAt.getTime();
    if (ta === tb)
        return 0;
    return ta < tb ? -1 : 1;
}
const RANKING_STEPS = [compareByFieldCount, compareByFieldPriority, compareByCreatedAt];
/**
 * Pick the most-specific scenario from a set of pre-routing candidates.
 * Applies the three-step deterministic tiebreak (field count → field priority
 * → createdAt asc).
 */
export function pickWinner(candidates) {
    if (candidates.length === 0)
        return undefined;
    if (candidates.length === 1)
        return candidates[0];
    return candidates.reduce((best, candidate) => {
        for (const cmp of RANKING_STEPS) {
            const result = cmp(best, candidate);
            if (result !== 0)
                return result < 0 ? best : candidate;
        }
        return best;
    });
}
//# sourceMappingURL=filter-condition-utils.js.map